import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useTranslations } from 'next-intl';

interface EditMessagePopupProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (newContent: string) => void;
  initialContent: string;
  messageId: string;
}

export function EditMessagePopup({
  isOpen,
  onClose,
  onConfirm,
  initialContent,
  messageId
}: EditMessagePopupProps) {
  const t = useTranslations('chat');
  const [content, setContent] = useState(initialContent);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Update content when initialContent changes
  useEffect(() => {
    setContent(initialContent);
  }, [initialContent]);

  // Focus textarea when popup opens
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
      // Select all text for easy editing
      textareaRef.current.select();
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (content.trim()) {
      onConfirm(content.trim());
      onClose();
    }
  };

  const handleCancel = () => {
    setContent(initialContent); // Reset to original content
    onClose();
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-[90vw] h-[80vh] max-w-4xl bg-[#1a1a1a] rounded-2xl border border-gray-700 shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white">{t('editMessage')}</h2>
          <button
            onClick={handleCancel}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 flex flex-col">
          <div className="flex-1 mb-4">
            <textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={t('enterYourMessage')}
              className="w-full h-full p-4 bg-[#2a2a2a] text-white placeholder-gray-400 border border-gray-600 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-[#5856d6] focus:border-transparent"
              style={{ minHeight: '300px' }}
            />
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-700">
            <button
              onClick={handleCancel}
              className="px-6 py-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-full transition-colors"
            >
              {t('cancel')}
            </button>
            <button
              onClick={handleConfirm}
              disabled={!content.trim()}
              className="px-6 py-2 bg-[#5856d6] hover:bg-[#5856d6]/80 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-full transition-colors"
            >
              {t('confirm')}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}